import { Product, productEndpoint } from '../models/product';
import { Onboarding, onboardingEndpoint, LeadSource, OnboardingType, Status, getStepNumber, getStepByNumber } from '../models/onboarding';
import { Promotion, promotionEndpoint } from '../models/promotion';
import { Cooperative, cooperativeEndpoint } from '../models/cooperatives';
import { Options, defaultOptions, optionEndpoint } from '../models/options';
import { FriendCode, friendEndpoint } from '../models/friend-code';
import { LightSupplier, lightSupplierEndpoint } from '../models/light-supplier';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, BehaviorSubject } from 'rxjs';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/of';
import * as moment from 'moment';
import { AlgGtmService } from './alg-gtm.service';
import { AbstractControl } from '@angular/forms';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import { EmployeeDiscount, employeeDiscountEndpoint } from '../models/employee-discount';
import { GMAPS_PLACES_AUTOCOMPLETE_URL, GMAPS_GEOCODE_URL, GMAPS_API_KEY } from '../config';
import { TranslationService } from 'angular-l10n';

@Injectable()
export class ApiService {
  public currentProduct: Observable<Product>;
  public nextProduct: BehaviorSubject<Product> = new BehaviorSubject<Product>({} as Product);
  public cooperatives: BehaviorSubject<Cooperative[]> = new BehaviorSubject<Cooperative[]>([] as Cooperative[]);
  public options: BehaviorSubject<Options> = new BehaviorSubject<Options>(defaultOptions);
  public friends: BehaviorSubject<FriendCode[]> = new BehaviorSubject<FriendCode[]>([] as FriendCode[]);
  public isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public onbError: BehaviorSubject<any> = new BehaviorSubject<any>({
    err: false,
    mess: '',
  });
  public currentOnboarding: Observable<Onboarding>;
  public currentLightGreen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public currentStep: Observable<Status>;
  public promotions: BehaviorSubject<Promotion[]> = new BehaviorSubject<Promotion[]>([] as Promotion[]);
  public lightSuppliers: BehaviorSubject<LightSupplier[]> = new BehaviorSubject<LightSupplier[]>([] as LightSupplier[]);
  public updateDocumentFront$ = new BehaviorSubject<boolean>(false);
  public updateDocumentBack$ = new BehaviorSubject<boolean>(false);
  public updateGreenEnergyPromo = new BehaviorSubject<boolean>(false);
  public isLibrettoValid$ = new BehaviorSubject<boolean>(undefined);
  public isLibrettoInputDisabled$ = new BehaviorSubject<boolean>(false);
  public isLibrettoCodeConfirmed$ = new BehaviorSubject<boolean>(false);
  public isUser75YearsOld$ = new BehaviorSubject<boolean>(false);
  public userFiscalCode$ = new BehaviorSubject<string>('');
  public onboardinFeId$ = new BehaviorSubject<any>('');

  private currentStepSubject: BehaviorSubject<Status> = new BehaviorSubject(Status.userInfo);
  private currentOnboardingSubject: BehaviorSubject<Onboarding> = new BehaviorSubject<Onboarding>({} as Onboarding);
  private currentProductSubject: BehaviorSubject<Product> = new BehaviorSubject<Product>({} as Product);
  private headers: HttpHeaders;
  private refreshToken: string;
  private uid: string;
  private token: string;
  private phpUrl = environment.api.phpBackendUrl;

  constructor(private http: HttpClient, private gtm: AlgGtmService, private translation: TranslationService) {
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=UTF-8',
    });
    this.currentStep = this.currentStepSubject.asObservable();
    this.currentOnboarding = this.currentOnboardingSubject.map((onboarding) => ({ ...onboarding }));
    this.currentProduct = this.currentProductSubject.map((product) => ({
      ...product,
    }));
    // Changes product id if currentProduct changes
    this.currentProduct
      .filter((product) => !!product && !!product._id)
      .withLatestFrom(this.nextProduct)
      .withLatestFrom(this.currentOnboarding)
      .subscribe(([[product, nextProduct], onboarding]: [[Product, Product], Onboarding]) => {
        if (onboarding) {
          this.saveOnboarding(
            {
              ...onboarding,
              product: product._id,
            },
            onboarding && onboarding.webStatus && onboarding.webStatus !== Status.userInfo
          );
        }
        if (nextProduct && product.slug == nextProduct.slug) {
          this.nextProduct.next(null);
          this.updateProductBySlug(product.slug);
        }
      });
  }

  private static getAutocompleteParams(input: string | ''): HttpParams {
    return new HttpParams().set('input', `${input}`).set('region', `it`).set('language', `it`).set('components', `country:it`).set('key', GMAPS_API_KEY);
  }

  private endpoint(entity: string) {
    return `${environment.api.basePath}/${entity}`;
  }

  private list(
    entity: string,
    page = 0,
    limit = 10,
    filters = {},
    options?: {
      loadRef?: string;
      sortField?: string;
      sortDirection?: 'asc' | 'desc';
    }
  ) {
    let params = new HttpParams();
    params = params.set('p', `${page}`);
    params = params.set('n', `${limit}`);
    if (options) {
      if (options.loadRef) {
        params = params.set('lr', options.loadRef);
      }
      if (options.sortField) {
        params = params.set('sf', options.sortField);
      }
      if (options.sortDirection) {
        params = params.set('sd', options.sortDirection);
      }
    }
    Object.keys(filters).forEach((k) => {
      params = params.set(`qry-${k}`, filters[k]);
    });
    const endpoint = this.endpoint(`${entity}s`);
    return this.http
      .get(endpoint, {
        headers: this.headers,
        params,
      })
      .map((obj) => {
        return obj[`${entity}s`] || [];
      });
  }

  private get(entity: string, id: string) {
    const endpoint = this.endpoint(`${entity}s/${id}`);
    return this.http
      .get(endpoint, {
        headers: this.headers,
      })
      .map((obj) => obj[entity] || {});
  }

  private getFeId(id: string) {
    const endpoint = this.endpoint(`single-onb/${id}`);
    return this.http
      .get(endpoint, {
        headers: this.headers,
      })
      .map((obj) => obj || '');
  }

  private save(entity: string, data: any) {
    const endpoint = this.endpoint(`${entity}s/${data._id || ''}`);
    if (data._id) {
      return this.http
        .put(endpoint, data, {
          headers: this.headers,
        })
        .map((obj) => obj[entity]);
    } else {
      return this.http
        .post(endpoint, data, {
          headers: this.headers,
        })
        .map((obj) => obj[entity]);
    }
  }

  private trackEvent(onboarding: Onboarding, ksProduct: Product) {
    try {
      let eventId = 'funnel_vps';
      const step = onboarding.webStatus;
      const onboardingId = onboarding._id;
      // Note: userLanguage is needed by IE
      window['tc_vars'].env_language = (window.navigator.language || '').split('-')[0];
      window['tc_vars'].env_work = environment.production ? 'prod' : 'preprod';
      window['tc_vars'].user_id = onboardingId;
      window['tc_vars']['product_array'][0].product_id = ksProduct.slug;
      window['tc_vars'].user_newcustomer = 1;
      window['tc_vars'].user_gender = ''; // TODO: get gender from CF
      window['tc_vars'].user_age = ''; // TODO: get age from CF
      window['tc_vars'].user_postalcode = onboarding.residenceCap ? onboarding.residenceCap : '';
      window['tc_vars'].user_address = onboarding.residenceAddress ? onboarding.residenceAddress : '';
      window['tc_vars'].user_firstname = onboarding.firstname ? onboarding.firstname : '';
      window['tc_vars'].user_lastname = onboarding.lastname ? onboarding.lastname : '';
      window['tc_vars'].user_email = onboarding.email ? onboarding.email : '';

      switch (step) {
        case Status.userInfo:
          window['tc_vars'].env_template = 'quote';
          break;
        case Status.callme:
          // funnel_vps
          window['tc_vars'].env_template = 'funnel_confirmation_contract';
          // lead_recall
          window['tc_vars'].env_template = '';
          eventId = 'lead_recall';
          break;
        case Status.complete:
          // funnel_vps
          window['tc_vars'].env_template = 'funnel_confirmation_contract';
          // last_button
          window['tc_vars'].env_template = '';
          eventId = 'last_button';
          break;
        default:
          window['tc_vars'].env_template = '';
          break;
      }
      // main event call

      // Handle sendMail case since it's not a proper step
      if (onboarding.sendEmail) {
        window['tc_vars'].env_template = '';
        eventId = 'clk_salva_sospendi';
      }
    } catch (e) {
      console.log(e);
    }
  }

  public setCurrentStep(step: Status) {
    this.currentStepSubject.next(step);
  }

  public updateProductBySlug(slug: string) {
    const apiObs = this.list(productEndpoint, 0, 1, { slug });
    apiObs
      .catch((e) => {
        console.warn('Error updating product by slug: ', e);
        return undefined;
      })
      .subscribe((products: Product[]) => {
        this.currentProductSubject.next(products[0] || ({} as Product));
      });
    return apiObs;
  }

  public updateProductById(id: any) {
    this.get(productEndpoint, id).subscribe((product: Product) => {
      this.currentProductSubject.next(product || ({} as Product));
    });
  }

  public updateNextProduct(filters: any) {
    this.list(productEndpoint, 0, 1, filters).subscribe((product: Product[]) => {
      this.nextProduct.next(product[0] || ({} as Product));
    });
  }

  public nextToCurrentProduct() {
    this.currentProductSubject.next(this.nextProduct.getValue());
  }
  public refreshCurrentProduct() {
    this.currentProductSubject.next({
      ...this.currentProductSubject.getValue(),
    });
  }
  public getFeIdOfOnboarding(id?: string) {
    let apiFeId = Observable.of(null);
    if (id) {
      apiFeId = this.getFeId(id);
      apiFeId.subscribe((response) => {
        this.onboardinFeId$.next(response || '');
      });
      return apiFeId;
    }
  }

  public updateOnboarding(id?: string) {
    let apiObs = Observable.of(null);
    if (id) {
      apiObs = this.get(onboardingEndpoint, id);
      apiObs.subscribe((onboarding: Onboarding) => {
        this.currentOnboardingSubject.next(onboarding || ({} as Onboarding));
      });
    }
    return apiObs;
  }
  public createOnboarding(onboarding: Onboarding) {
    this.isLoading.next(true);
    if (onboarding.webStatus === Status.callme) {
      this.gtm.callmeEvent(true);
      this.trackEvent(onboarding, this.currentProductSubject.getValue());
    }
    delete onboarding._id;
    if (typeof onboarding.product !== 'string') {
      if (onboarding.product) onboarding.product = onboarding.product._id;
    }
    return this.save(onboardingEndpoint, onboarding).map((onb) => {
      this.isLoading.next(false);
      return onb;
    });
  }
  public saveOnboarding(onboarding: Onboarding, server?: boolean) {
    this.isLoading.next(true);
    onboarding.lightGreen = this.currentLightGreen.getValue();
    onboarding.product = this.currentProductSubject.getValue()._id;
    if (server) {
      if (!onboarding.leadSource) {
        onboarding.leadSource = LeadSource.drops;
      } else if (onboarding.leadSource === LeadSource.blacklist) {
        this.onbError.next({
          err: true,
          mess: this.translation.translate('BLACKLIST_ERROR_MESSAGE'),
        });
      }

      if (!onboarding.typeId) {
        onboarding.typeId = OnboardingType.onboarding;
      }
      if (onboarding.sendEmail) {
        this.gtm.saveEvent(true);
      }
      const data = { ...onboarding };
      data.gasBill = undefined;
      data.lightBill = undefined;
      this.save(onboardingEndpoint, data).subscribe(
        (onb: Onboarding) => {
          onboarding._id = onb._id;
          localStorage.setItem('feId', onb.feId);
          this.trackEvent(onboarding, this.currentProductSubject.getValue());
          if (onb.webStatus !== Status.callme) {
            const product = this.currentProductSubject.getValue();
            this.gtm.stepEvent(onb.webStatus, product.family.name, product.type);
          }
          if (onb.leadSource === LeadSource.blacklist) {
            this.onbError.next({
              err: true,
              mess: this.translation.translate('BLACKLIST_ERROR_MESSAGE'),
            });
          }
          if (onb.webStatus === Status.complete) {
            this.gtm.submitEvent();
          }
          this.currentOnboardingSubject.next(onb);
          this.isLoading.next(false);
        },
        (err) => {
          console.log(err);
          this.isLoading.next(false);
          this.onbError.next({
            err: true,
            mess: 'Spiacenti, sembra che ci sia qualche difficoltà. Contattaci al numero verde 800-089902',
          });
        }
      );
    } else {
      this.currentOnboardingSubject.next(onboarding);
      this.isLoading.next(false);
    }
  }
  public nextStep(onboarding: Onboarding, currentStep) {
    const currentStepNumber = getStepNumber(currentStep);
    const nextStepNumber = currentStepNumber + 1;
    const lastStepNumber = getStepNumber(onboarding.webStatus || 'user_info');
    const nextStep = nextStepNumber > lastStepNumber ? getStepByNumber(nextStepNumber) : onboarding.webStatus;
    this.setCurrentStep(nextStep);
    this.saveOnboarding(
      {
        ...onboarding,
        webStatus: nextStep,
      },
      true
    );
  }

  public previoustStep(onboarding: Onboarding, currentStep) {
    const currentStepNumber = getStepNumber(currentStep);
    const nextStepNumber = currentStepNumber - 1;
    const lastStepNumber = getStepNumber(onboarding.webStatus || 'user_info');
    const nextStep = nextStepNumber < lastStepNumber ? getStepByNumber(nextStepNumber) : onboarding.webStatus;
    this.setCurrentStep(nextStep);
    this.saveOnboarding(
      {
        ...onboarding,
        webStatus: nextStep,
      },
      true
    );
  }

  public getCooperatives(filters: any = {}) {
    this.list(cooperativeEndpoint, 0, 10, filters).subscribe((cooperative: Cooperative[]) => {
      this.cooperatives.next(cooperative);
    });
    this.isLoading.next(false);
  }
  public getPromotions(filters: any = {}, duration: string = 'Tutte') {
    this.list(promotionEndpoint, 0, 100, filters).subscribe((promotion: Promotion[]) => {
      const today = Date.now();
      const validPromotion: Promotion[] = [];
      promotion.map((e) => {
        const startDate = moment(e.startPromo).format();
        const endDate = moment(e.endPromo).format();
        const timestampStart = moment(startDate).valueOf();
        const timestampEnd = moment(endDate).valueOf();
        e.startPromo = timestampStart.toString();
        e.endPromo = timestampEnd.toString();
        if (moment(today) >= moment(+timestampStart) && moment(today) <= moment(+timestampEnd)) {
          // if(e.duration != 'Tutte'){
          //   if(e.duration == duration){
          //     validPromotion.push(e);
          //   }
          // }else{
          //   validPromotion.push(e);
          // }
          validPromotion.push(e);
        }
      });
      this.promotions.next(validPromotion);
    });
  }

  public getOptions(filters: any = {}) {
    this.list(optionEndpoint, 0, 10, filters).subscribe((options: any[]) => {
      this.options.next(
        options.reduce((accumulator, { key, value }) => {
          let v = value;
          switch (typeof defaultOptions[key]) {
            case 'boolean':
              v = value === 'true';
              break;
          }
          return { ...accumulator, [key]: v };
        }, defaultOptions)
      );
    });
  }
  public async checkFriend(friendCode: string): Promise<any> {
    return await this.http.get(this.endpoint(`check-friend/${friendCode}`)).toPromise();
  }

  public async checkFriendLiveFromSF(friendCode: string, onboardingId: string): Promise<any> {
    return await this.http.get(this.endpoint(`check-friend-live/${onboardingId}/${friendCode}`)).toPromise();
  }

  public getLightSuppliers() {
    this.list(lightSupplierEndpoint, 0, 500).subscribe((lightSuppliers: LightSupplier[]) => {
      this.lightSuppliers.next(lightSuppliers);
    });
  }

  public geocodeCity(city: string): Observable<object> {
    const params = new HttpParams().set('address', `${city}`).set('region', `it`).set('components', `country:it`).set('key', GMAPS_API_KEY);

    return this.http.get(GMAPS_GEOCODE_URL, { params });
  }

  public getAddresses(input: string) {
    const params = ApiService.getAutocompleteParams(input).set('types', `address`);

    return this.http
      .get(GMAPS_PLACES_AUTOCOMPLETE_URL, {
        params,
      })
      .map((results: any) => {
        return results.predictions.map((element) => ({
          value: element.description,
          label: element.description,
        }));
      })
      .catch((err) => {
        return new BehaviorSubject<any[]>([]);
      });
  }

  public getCities(type: 'cap' | 'city' | 'province', term: string) {
    let params = ApiService.getAutocompleteParams(term);

    if (type === 'city') {
      params = params.set('types', `(cities)`);
    } else if (type === 'province' || type === 'cap') {
      params = params.set('types', `(regions)`);
    }

    return this.http
      .get(GMAPS_PLACES_AUTOCOMPLETE_URL, {
        params,
      })
      .map((results: any) => {
        return results.predictions.map((element) => ({
          value: element.terms[0].value,
          label: element.terms[0].value,
        }));
      })
      .catch((err) => {
        return new BehaviorSubject<any[]>([]);
      });
  }

  // Save Employee Discount
  public saveDiscount(employeeDiscount: EmployeeDiscount) {
    const data = { ...employeeDiscount };
    const endpoint = this.endpoint(`${employeeDiscountEndpoint}s/saveEmployee`);
    return this.http
      .post(endpoint, data, {
        headers: this.headers,
      })
      .map((res: any) => res)
      .catch((err) => Observable.of({ success: false }));
  }

  public forceSaveDiscount(employeeDiscount: EmployeeDiscount) {
    const data = { ...employeeDiscount };
    const endpoint = this.endpoint(`${employeeDiscountEndpoint}s/saveEmployeeForce`);
    return this.http
      .post(endpoint, data, {
        headers: this.headers,
      })
      .map((res: any) => res)
      .catch((err) => Observable.of({ success: false }));
  }

  public sendMeOtpCode(phone_number: number, id: string, consenso: boolean | string = false) {
    const endpoint = this.endpoint(`otp/${id}/${phone_number}/${consenso}`);
    return this.http
      .get(endpoint, {
        headers: this.headers,
      })
      .map((res: any) => res)
      .catch((err) => Observable.of({ success: false, ...err }));
  }

  public checkOtpCode(code: string, id: string, consenso: boolean | string = false) {
    const endpoint = this.endpoint(`opt/check/code/${id}/${code}/${consenso}`);
    return this.http.get(endpoint, {
      headers: this.headers,
    });
  }

  public checkWipStatus() {
    const endpoint = this.endpoint(`maintenance-periods/current`);
    return this.http.get(endpoint, {
      headers: this.headers,
    });
  }

  public getCTE(data: any) {
    const endpoint = this.endpoint(
      `cte/${data.product}/${data.promo}/${data.priceTypology}/${data.offerType}/${data.commodity}/${data.duration}/${data.placet}/${data.renew}`
    );
    return this.http.get(endpoint, {
      headers: this.headers,
      responseType: 'arraybuffer',
    });
  }

  public getCGF(data: any) {
    const endpoint = this.endpoint(`cgf/${data.unipol}/${data.placet}/${data.commodity}`);

    return this.http.get(endpoint, {
      headers: this.headers,
      responseType: 'arraybuffer',
    });
  }

  public getCGFVulnerableProduct() {
    const endpoint = this.endpoint(`cgf-vulnerable`);

    return this.http.get(endpoint, {
      headers: this.headers,
      responseType: 'arraybuffer',
    });
  }

  public getVulnerabilitySummarySheet() {
    const endpoint = this.endpoint(`vulnerability-summary-sheet`);

    return this.http.get(endpoint, {
      headers: this.headers,
      responseType: 'arraybuffer',
    });
  }

  public getFaqList() {
    const endpoint = this.endpoint(`faq/list`);
    // const endpoint = `http://localhost:5000/api/faq/list`;

    return this.http.get(endpoint, {
      headers: this.headers,
    });
  }

  public checkBookletValidity(fiscalCode: string, bookletCode: string) {
    const data = {
      fiscalCode: fiscalCode,
      bookletCode: bookletCode,
    };
    const endpoint = this.endpoint(`validate-libretto/${fiscalCode}/${bookletCode}`);
    this.isLoading.next(true);
    return this.http
      .post(endpoint, data, {
        headers: this.headers,
      })
      .map((res: any) => {
        this.isLoading.next(false);
        return res;
      })
      .catch((err) => {
        this.isLoading.next(false);
        return Observable.of({ success: false });
      });
  }

  public checkNumberContracts(fiscalCode: string) {
    const data = {
      fiscalCode: fiscalCode,
    };
    const endpoint = this.endpoint(`check-number-contracts/${fiscalCode}`);
    this.isLoading.next(true);
    return this.http
      .post(endpoint, data, {
        headers: this.headers,
      })
      .map((res: any) => {
        this.isLoading.next(false);
        return res;
      })
      .catch((err) => {
        this.isLoading.next(false);
        return Observable.of({ success: false });
      });
  }

  public checkCoopMemberRegistry(eanCard: string) {
    const data = {
      ean_card: eanCard,
    };
    const endpoint = this.endpoint(`check-socio-registry/${eanCard}`);
    this.isLoading.next(true);
    return this.http
      .post(endpoint, data, {
        headers: this.headers,
      })
      .map((res: any) => {
        this.isLoading.next(false);
        return res;
      })
      .catch((err) => {
        this.isLoading.next(false);
        return Observable.of({ success: false });
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/api.service.ts